import { ForwardRefExoticComponent, SVGProps } from 'react'

import { GroupBy } from '@factorialco/f0-react/icons/app'
import { FormsElementInput, FormsElementTypeEnum } from 'generated/resources/datatable.graphql'

import i18n from 'modules/core/lib/i18n'

import { Element } from 'modules/forms/lib/Element'

type optionsProp = {
  label: string
  description?: string
  locked?: boolean
}

/**
 * SectionElement class for grouping other elements
 */
export class SectionElement extends Element {
  /**
   * Collection of elements in the section
   */
  elements: Element[] = []

  /**
   * Create a new section element
   *
   * @param label - The label of the section
   * @param options - The options for the section
   * @returns A new section element instance
   */
  static create({ label, description = '', locked = false }: optionsProp): SectionElement {
    const el = new SectionElement({
      label,
      description,
      mandatory: false, //locked element are always mandatory
    })
    if (locked) {
      el.lock()
    }

    return el
  }

  /**
   * Get the name of the element
   */
  getName(): string {
    return i18n.t('forms.modals.elements.section')
  }

  /**
   * Get the type of the element
   */
  getType(): FormsElementTypeEnum {
    return 'section'
  }

  /**
   * Get the icon for the element
   */
  getIcon(): ForwardRefExoticComponent<SVGProps<SVGSVGElement>> {
    return GroupBy
  }

  /**
   * Add an element to the section
   *
   * @param element - The element to add
   * @returns The section element instance
   */
  addElement(element: Element): SectionElement {
    element.sectionId = this.id
    element.position = this.elements.length
    this.elements.push(element)
    this.sortElements()
    return this
  }

  /**
   * Remove an element from the section
   *
   * @param elementId - The ID of the element to remove
   */
  removeElement(elementId: string): void {
    this.elements = this.elements.filter((element) => element.id !== elementId)
    this.updatePositions()
  }

  /**
   * Get an element by its ID
   *
   * @param elementId - The ID of the element to get
   * @returns The element with the specified ID, or undefined if not found
   */
  getElement(elementId: string): Element | undefined {
    return this.elements.find((element) => element.id === elementId)
  }

  /**
   * Sort elements by their position
   */
  sortElements(): void {
    this.elements.sort((a, b) => a.position - b.position)
  }

  /**
   * Update positions of all elements to ensure they are sequential
   */
  updatePositions(): void {
    this.elements.forEach((element, index) => {
      element.position = index
    })
  }

  /**
   * Override `toBackend()` to include child elements
   */
  toBackend(): FormsElementInput {
    return {
      ...super.toBackend(),
      elements: this.elements.map((el) => el.toBackend()),
    }
  }
}
